import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { EDIT_OBJECT_TOKENS } from './editObjectTokens';

export interface FavoritePill {
  /** Stable identifier (e.g. CV item id). */
  id: string;
  /** Visible chip text. */
  label: string;
  /** True when an entry with this label is already on the active list. */
  active?: boolean;
}

export interface FavoritesPillStripProps {
  title?: string;
  pills: ReadonlyArray<FavoritePill>;
  onPillClick: (pill: FavoritePill) => void;
  /** Optional remove handler — when provided, hover shows an × button. */
  onPillRemove?: (pill: FavoritePill) => void;
  /** Empty-state copy shown when pills is empty. */
  emptyHint?: string;
}

/**
 * Inline strip of pill buttons used at the top of the Awards step (and
 * reusable for any list step that pulls favorites from the user's CV).
 * Active pills (matching an entry already in the list) render filled in
 * Ali Red; inactive pills render as outlined chips. Click pre-fills /
 * adds a row in the parent list.
 */
export const FavoritesPillStrip: React.FC<FavoritesPillStripProps> = ({
  title = 'Favorites',
  pills,
  onPillClick,
  onPillRemove,
  emptyHint = 'No favorites saved yet. Save items as favorites to access them here.',
}) => {
  return (
    <Box data-testid="favorites-pill-strip" sx={{ mb: 2 }}>
      <Typography
        variant="overline"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: EDIT_OBJECT_TOKENS.color.slate500,
          display: 'block',
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {pills.length === 0 ? (
        <Typography
          variant="caption"
          sx={{
            fontStyle: 'italic',
            color: EDIT_OBJECT_TOKENS.color.slate300,
            display: 'block',
            py: 0.75,
          }}
        >
          {emptyHint}
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          {pills.map((pill) => (
            <Box
              key={pill.id}
              component="button"
              type="button"
              onClick={() => onPillClick(pill)}
              data-testid={`favorite-pill-${pill.id}`}
              data-active={pill.active ? 'true' : 'false'}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                py: 0.625,
                pl: 1.5,
                pr: onPillRemove ? 0.75 : 1.25,
                border: `1px solid ${
                  pill.active ? EDIT_OBJECT_TOKENS.color.aliRed700 : EDIT_OBJECT_TOKENS.color.line
                }`,
                bgcolor: pill.active ? EDIT_OBJECT_TOKENS.color.aliRed700 : '#fff',
                color: pill.active ? '#fff' : EDIT_OBJECT_TOKENS.color.ink700,
                borderRadius: 999,
                fontFamily: EDIT_OBJECT_TOKENS.font.sans,
                fontSize: 13,
                cursor: 'pointer',
                transition: `all ${EDIT_OBJECT_TOKENS.motion.durationMs}ms ${EDIT_OBJECT_TOKENS.motion.ease}`,
                '&:hover': {
                  borderColor: EDIT_OBJECT_TOKENS.color.aliRed700,
                  bgcolor: pill.active
                    ? EDIT_OBJECT_TOKENS.color.aliRed800
                    : EDIT_OBJECT_TOKENS.color.aliRed050,
                  color: pill.active ? '#fff' : EDIT_OBJECT_TOKENS.color.aliRed700,
                },
              }}
            >
              {pill.label}
              {onPillRemove && (
                <Box
                  component="span"
                  role="button"
                  aria-label={`Remove ${pill.label} from favorites`}
                  data-testid={`favorite-pill-remove-${pill.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPillRemove(pill);
                  }}
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    display: 'inline-grid',
                    placeItems: 'center',
                    color: 'inherit',
                    opacity: 0.7,
                    '&:hover': { opacity: 1, color: EDIT_OBJECT_TOKENS.color.danger },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};
